'use strict';

import axios from 'axios';
import { listenDOMEvent } from '@jspartials/dom-api-utils';

// Selectors
const selector = {
  klarnaMessaging: '.js-klarna-messaging',
  klarnaMessagingLoader: '.js-klarna-messaging-loader',
  inputPayments: 'product-details-payments'
};

// Elements
const element = {
  klarnaMessaging: null,
  klarnaMessagingLoader: null,
  inputPayments: null
};

// Variables
let klarnaMessagingShowed = false;
let PaymentMessageElement = null;
let stripeScriptPromise = null;

/**
 * Ensures Stripe.js is loaded and ready, loading it on demand
 * @returns {Promise<object>}
 */
const ensureStripeScript = () => {
  if (window.Stripe) {
    return Promise.resolve(window.Stripe);
  }

  if (!stripeScriptPromise) {
    stripeScriptPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(
        `script[src="${globalVariables.stripe.scriptURL}"]`
      );

      if (!script) {
        script = document.createElement('script');
        script.src = globalVariables.stripe.scriptURL;
        script.async = true;
        document.head.appendChild(script);
      }

      script.addEventListener('load', () => resolve(window.Stripe));
      script.addEventListener('error', reject);
    });
  }

  return stripeScriptPromise;
};

const initElements = () => {
  element.klarnaMessaging =
    document.querySelector(selector.klarnaMessaging) || null;
  element.klarnaMessagingLoader =
    document.querySelector(selector.klarnaMessagingLoader) || null;
  element.inputPayments =
    document.getElementById(selector.inputPayments) || null;
};

const initKlarnaMessagingCart = () => {
  initElements();
  showKlarnaMessaging();
};

const initKlarnaMessagingPdp = () => {
  initElements();

  klarnaMessagingShowed = false;

  if (element.inputPayments) {
    listenDOMEvent(element.inputPayments, 'change', () => {
      if (element.inputPayments.checked && !klarnaMessagingShowed) {
        showKlarnaMessaging();
      }
    });

    if (element.inputPayments.checked) {
      showKlarnaMessaging();
    }
  }
};

const showKlarnaMessaging = () => {
  initElements();

  if (element.klarnaMessaging) {
    const amount = element.klarnaMessaging.dataset.amount;
    const amountFormatted = amount ? parseInt(amount, 10) : 0;

    if (amountFormatted > 0) {
      Promise.all([
        axios.post(globalVariables.endpoint.klarnaMessaging),
        ensureStripeScript()
      ])
        .then(([response, StripeLib]) => {
          const { data } = response;
          const { countryCode, currency, key, locale, visible } = data;

          if (!visible) {
            if (element.klarnaMessagingLoader) {
              element.klarnaMessagingLoader.remove();
            }
            return;
          }

          const stripe = StripeLib(key, {
            locale: locale
          });
          const elements = stripe.elements();
          const options = {
            amount: amountFormatted,
            currency: currency,
            countryCode: countryCode,
            paymentMethodTypes: ['klarna']
          };

          PaymentMessageElement = elements.create(
            'paymentMethodMessaging',
            options
          );
          PaymentMessageElement.on('ready', () => {
            if (element.klarnaMessagingLoader) {
              element.klarnaMessagingLoader.remove();
            }
          });
          PaymentMessageElement.mount(element.klarnaMessaging);
          klarnaMessagingShowed = true;
        })
        .catch((error) => {
          if (element.klarnaMessagingLoader) {
            element.klarnaMessagingLoader.remove();
          }
          console.error(error);
        });
    } else {
      if (element.klarnaMessagingLoader) {
        element.klarnaMessagingLoader.remove();
      }
      console.warn('Klarna messaging is not available');
    }
  }
};

const updateAmountKlarnaMessaging = (amount) => {
  if (PaymentMessageElement) {
    PaymentMessageElement.update({
      amount: amount
    });
  }
};

export {
  initKlarnaMessagingCart,
  initKlarnaMessagingPdp,
  updateAmountKlarnaMessaging
};
